/**
 * UserCourseProgressService
 * Per-user per-course completion state. Drives the volunteer dashboard + the
 * course-detail "Start | Resume | Completed" CTA.
 * Route: api/user-course-progress
 */
import { BaseService } from '../base/BaseService';
export class UserCourseProgressService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? UserCourseProgressService.DEFAULT_SERVICE_PATH,
            basePath: UserCourseProgressService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    /** My progress across all courses (current signed-in user). */
    async getMine() {
        const response = await this.getMineRaw();
        return response.data ?? [];
    }
    getMineRaw() {
        return this.http.get(this.buildUrl('me'));
    }
    /** My progress for a specific course. */
    async getMineForCourse(courseId) {
        const response = await this.getMineForCourseRaw(courseId);
        return response.data ?? null;
    }
    getMineForCourseRaw(courseId) {
        return this.http.get(this.buildUrl(`me/course/${courseId}`));
    }
    /**
     * My computed progress for a course — completion derived live from
     * per-session progress. Authoritative source for "X of Y sessions" and
     * whether the course is completed.
     */
    async getMyComputedForCourse(courseId) {
        const response = await this.getMyComputedForCourseRaw(courseId);
        return response.data ?? null;
    }
    getMyComputedForCourseRaw(courseId) {
        return this.http.get(this.buildUrl(`me/course/${courseId}/computed`));
    }
    /** My computed progress across every course I've touched. */
    async getMyComputed() {
        const response = await this.getMyComputedRaw();
        return response.data ?? [];
    }
    getMyComputedRaw() {
        return this.http.get(this.buildUrl('me/computed'));
    }
    /**
     * My learning — one enriched row per course I've started, carrying course
     * name + thumbnail, live computed completion %, completed/total session
     * counts, the resume session and a localized progress status. Lets a
     * "My Learning" surface render in a single call without joining the catalogue.
     */
    async getMineView() {
        const response = await this.getMineViewRaw();
        return response.data ?? [];
    }
    getMineViewRaw() {
        return this.http.get(this.buildUrl('me/view'));
    }
    /** Admin: every course progress row for a given user. */
    async getForUser(userId) {
        const response = await this.getForUserRaw(userId);
        return response.data ?? [];
    }
    getForUserRaw(userId) {
        return this.http.get(this.buildUrl(`user/${encodeURIComponent(userId)}`));
    }
    /** Admin/Leader: every learner's progress on a given course. */
    async getForCourse(courseId) {
        const response = await this.getForCourseRaw(courseId);
        return response.data ?? [];
    }
    getForCourseRaw(courseId) {
        return this.http.get(this.buildUrl(`course/${courseId}`));
    }
    /** Has (userId, courseId) completed? Convenience for prereq gating UIs. */
    async hasCompleted(userId, courseId) {
        const response = await this.http.get(this.buildUrl(`user/${encodeURIComponent(userId)}/course/${courseId}/has-completed`));
        return response.data === true;
    }
    /** Admin manual upsert (ping/complete endpoints are the normal path). */
    async upsert(item) {
        const response = await this.upsertRaw(item);
        return response.data ?? null;
    }
    upsertRaw(item) {
        return this.http.post(this.buildUrl(''), item);
    }
    async delete(id) {
        const response = await this.deleteRaw(id);
        return response.success;
    }
    deleteRaw(id) {
        return this.http.delete(this.buildUrl(`${id}`));
    }
}
UserCourseProgressService.DEFAULT_SERVICE_PATH = 'resources';
UserCourseProgressService.BASE_PATH = 'api/user-course-progress';
//# sourceMappingURL=UserCourseProgressService.js.map